import { Component, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import mixpanel from 'mixpanel-browser';
import isEmpty from 'lodash/isEmpty';
import { UiStackControllerService } from './cc-api/services/ui-stack-controller.service';
import { RegisterSvgIconsService } from './services/register-svg-icons.service';
import { CookieUtilWrapperService } from './services/cookie-util-wrapper.service';
import { FeatureFlagsService } from './services/feature-flags.service';
import { FaviconService } from './services/favicon.service';
import { PublicApIsService } from 'src/app/cc-api/services/public-ap-is.service';
import { environment } from '../environments/environment';
import Hotjar from '@hotjar/browser';
import { LocalStorageService } from './services/local-storage.service';
import { ApplicationControllerService } from './cc-api/services';
import { SimpleOauth2User } from './cc-api/models';

const MIXPANEL_TOKEN = 'cafa7db55b0c5ddb0b7b2c9d2ebf3805';

const HOTJAR_VERSION = 6;

@Component({
  selector: 'app-root',
  template: '<router-outlet *ngIf="featureFlagsLoaded"></router-outlet>',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
  currentBlueprintEnvironmentURL: any;
  featureFlagsLoaded: boolean = false;
  customTheme = null;
  constructor(
    private router: Router,
    private titleService: Title,
    private uiStackControllerService: UiStackControllerService,
    private localStorage: LocalStorageService,
    private cookieService: CookieUtilWrapperService,
    private iconPack: RegisterSvgIconsService,
    private featureFlagsService: FeatureFlagsService,
    private faviconService: FaviconService,
    private publicApiService: PublicApIsService,
    private applicationControllerService: ApplicationControllerService,
  ) {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: !environment.production,
      track_pageview: 'full-url',
      persistence: 'localStorage',
    });
    this.iconPack.register();
    this.attachDynamicTitlesListener();
    this.currentBlueprintEnvironmentURL = this.cookieService.get('selection');
    this.makeFeatureFlagsCall();
  }

  /**
   * Subscribe to route change events and filter out NaviagtionEnd event.
   * Navigate to the deepest child route and access the title property from data object
   * Set title using Angular's Title service
   * @private
   */
  private attachDynamicTitlesListener() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        let root = this.router.routerState.snapshot.root;
        while (root) {
          if (root?.children && root?.children?.length) {
            root = root?.children[0];
          } else if (root?.data && root?.data?.title && root?.params) {
            // If running locally set clientName to 'Dev' else extract client name from hostname.
            const clientName =
              window.location.hostname === 'localhost'
                ? 'Dev'
                : window.location.hostname.split('.')[0];
            this.titleService.setTitle(`${root?.data?.title}`);

            this.setCurrentBlueprintEnvironmentURL(root);
            return;
          } else {
            return;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.featureFlagsService?.resetData();
  }

  private setCurrentBlueprintEnvironmentURL(root) {
    const p = root.params;
    if (
      p.stackName &&
      p.clusterId &&
      this.currentBlueprintEnvironmentURL.indexOf(p.clusterId) === -1
    ) {
      this.currentBlueprintEnvironmentURL =
        '/' + p.stackName + '/cluster/' + p.clusterId;
      // this.cookieService.set('selection', this.currentBlueprintEnvironmentURL, {
      //   domain: window.location.hostname,
      //   path: '/',
      // });
      this.cookieService.set(
        'selection',
        this.currentBlueprintEnvironmentURL,
        undefined,
        '/',
      );
    } else if (
      p.stackName &&
      !p.clusterId &&
      this.currentBlueprintEnvironmentURL.indexOf(p.stackName) === -1
    ) {
      //get all cluster of selected stack and set first cluster as default cluster in cookie
      this.uiStackControllerService.getClustersUsingGET1(p.stackName).subscribe(
        (c) => {
          if (c?.length) {
            let clusterId = c[0].id;
            this.currentBlueprintEnvironmentURL =
              '/' + p.stackName + '/cluster/' + clusterId;
            this.cookieService.set(
              'selection',
              this.currentBlueprintEnvironmentURL,
              undefined,
              '/',
            );
          }
        },
        (error) => {
          if (error) {
            this.currentBlueprintEnvironmentURL =
              '/' + p.stackName + '/cluster';
            this.cookieService.set(
              'selection',
              this.currentBlueprintEnvironmentURL,
              undefined,
              '/',
            );
          }
        },
      );
    }
    return;
  }
  async makeFeatureFlagsCall() {
    this.featureFlagsLoaded = false;
    try {
      await this.featureFlagsService?.getAllFeatureFlags();
      const isMercedesCP = this.featureFlagsService?.isMercedesCP;
      if (!isMercedesCP) {
        this.initialiseHotjar();
      }
      this.featureFlagsLoaded = true;
    } catch (error) {
      this.featureFlagsLoaded = true;
    }
    this.updateLogoutUrl();
    this.setFavIconTitle();
    if (this.featureFlagsService?.allowThemeCustomization()) {
      this.getCustomThemeContent();
    }
  }

  updateLogoutUrl() {
    const logoutURL = this.featureFlagsService.getLogoutUrl();
    const isMercedesCP = this.featureFlagsService?.isMercedesCP;
    if (logoutURL && isMercedesCP) {
      try {
        localStorage?.setItem('logoutUrl', logoutURL);
      } catch (err) {}
    }
  }

  initialiseHotjar() {
    Hotjar.init(this.featureFlagsService.getHotjarSiteId(), HOTJAR_VERSION);
  }

  getCustomThemeContent() {
    this.publicApiService.retrieveThemeFileUsingGET().subscribe((data) => {
      try {
        this.customTheme = data?.content ? JSON.parse(data?.content) : null;
        if (!isEmpty(this.customTheme)) {
          this.setCustomThemeOnDom();
        }
      } catch (error) {
        console.log('Unable to load theme', error);
      }
    });
  }

  setCustomThemeOnDom() {
    const bodyElement = document.body.style;
    Object.keys(this.customTheme).forEach((key) => {
      bodyElement.setProperty(`--${key}`, this.customTheme[key]);
    });
  }

  setFavIconTitle() {
    const favIcon = this.featureFlagsService.getFavIcon();
    const browserTitle = this.featureFlagsService.browserTitle;
    if (favIcon) {
      this.faviconService.setFavicon(favIcon);
    }
    if (browserTitle) {
      this.titleService.setTitle(browserTitle);
    }
  }
}
